// Export: the complete matching set, in visible column order, as plain data values. A grouped instance
// exports group by group in group order, sorted within each group.

import { exportValue } from '../format'
import { groupSections } from '../grouped/grouping'
import { viewKeys } from '../view/view'
import { runQuery } from './run'

const exportsOf = column => {
  if (column.type === 'actions') return false
  if (column.capabilities) return Boolean(column.capabilities.export)
  return column.exportable !== false && (!column.render || column.value)
}

// Visible column keys in display order, from table state ({view}) or published state ({columns}).
export const visibleKeys = (columns, state = {}) => {
  const keys = columns.map(column => column.key)
  if (Array.isArray(state.columns))
    return state.columns.filter(key => keys.includes(key))
  // the rendered order: locked columns at their edges, as the View places them
  return viewKeys(columns, state.view)
}

export const exportColumns = (columns, state) =>
  visibleKeys(columns, state)
    .map(key => columns.find(column => column.key === key))
    .filter(exportsOf)

const queryOf = (state = {}) =>
  state.query || {
    search: state.search || '',
    filters: state.filters || {},
    sort: state.sort,
  }

const hiddenOf = (columns, state) => {
  const visible = visibleKeys(columns, state)
  return columns.map(c => c.key).filter(key => !visible.includes(key))
}

/**
 * Exports the complete matching set of a client-backed source. Each row is an object keyed by column key in
 * visible order; values are the declared export values with no links, controls or icons.
 * @param {object[]} rows the complete client set
 * @param {object[]} columns
 * @param {object} state table state or published state
 * @param {string} [locale]
 * @param {?object} [grouped] the normalized grouped configuration of a grouped instance
 */
export const exportRows = (rows, columns, state, locale, grouped = null) => {
  const cols = exportColumns(columns, state)
  const sorted = runQuery(rows, columns, queryOf(state), locale, {
    hidden: hiddenOf(columns, state),
  })
  const matching = grouped
    ? groupSections(grouped, sorted).flatMap(section => section.rows)
    : sorted
  return matching.map(row => {
    const out = {}
    cols.forEach(column => {
      out[column.key] = exportValue(column, row)
    })
    return out
  })
}
